/**
 * App-start wiring for Premium Session Recording on the native shells: builds
 * the native recorder + upload queue, registers them as the session-recording
 * adapters (the UI only ever talks to the ports), starts the queue (resumes
 * uploads left from a previous run) and runs the 7-day local-audio sweep at
 * start and on every return to the foreground. Web keeps the unavailable
 * fallbacks. Called once from `main.tsx`.
 */

import { SESSION_RECORDING_AUDIO_MAX_DAYS } from '@shared/sessions/session-recording'
import { whenAuthStorageReady } from '../../../config/amplify'
import { SecureStorage } from '../../../services/secure-storage'
import {
  SESSION_RECORDING_UPLOAD_QUEUE_KEY_PREFIX,
  claimSessionRecordingAudio,
  isSessionRecorderAvailable,
  joinRecording,
  onSessionRecorderDeviceWipe,
  sweepProductionRecordings,
  uploadRecordingPart,
} from '../../../services/session-recorder'
import { registerSessionRecordingAdapters } from './adapters'
import { createNativeSessionAudioRecorder } from './native-session-audio-recorder'
import {
  createNativeSessionRecordingUploader,
  type NativeSessionRecordingUploader,
  type UploaderNetworkStatus,
} from './native-session-recording-uploader'
import {
  completeSessionRecordingUpload,
  listSessionRecordings,
  startSessionRecordingUpload,
} from './session-recordings-api'

const AUDIO_MAX_AGE_MS = SESSION_RECORDING_AUDIO_MAX_DAYS * 24 * 60 * 60 * 1000

let registered = false

async function currentUserId(): Promise<string | null> {
  // Cold start: the native token store must be migrated/loaded before the first read,
  // or a signed-in user reads as signed out (and their queue as empty).
  await whenAuthStorageReady()
  const { getCurrentUser } = await import('aws-amplify/auth')
  let userId: string
  try {
    userId = (await getCurrentUser()).userId
  } catch (err) {
    if ((err as { name?: unknown } | null)?.name === 'UserUnAuthenticatedException') return null
    throw err
  }
  // Before this user's queue loads: audio another account left on the device goes.
  await claimSessionRecordingAudio(userId).catch(() => undefined)
  return userId
}

function toNetworkStatus(raw: { connected: boolean; connectionType: string }): UploaderNetworkStatus {
  const type = raw.connectionType
  return {
    connected: raw.connected,
    connectionType: type === 'wifi' || type === 'cellular' || type === 'none' ? type : 'unknown',
  }
}

async function getNetworkStatus(): Promise<UploaderNetworkStatus> {
  const { Network } = await import('@capacitor/network')
  return toNetworkStatus(await Network.getStatus())
}

function onNetworkChange(listener: (status: UploaderNetworkStatus) => void): () => void {
  let cancelled = false
  let remove: (() => void) | null = null
  void import('@capacitor/network')
    .then(({ Network }) =>
      Network.addListener('networkStatusChange', (status) => listener(toNetworkStatus(status))),
    )
    .then((handle) => {
      if (cancelled) void handle.remove()
      else remove = () => void handle.remove()
    })
    .catch(() => {
      /* plugin missing in older shells — interval + online events cover it */
    })
  return () => {
    cancelled = true
    remove?.()
  }
}

/** Register the native recorder + uploader once (no-op on the web). */
export function registerNativeSessionRecordingAdapters(): void {
  if (registered || !isSessionRecorderAvailable()) return
  registered = true

  let uploader: NativeSessionRecordingUploader | null = null
  const recorder = createNativeSessionAudioRecorder({
    isTracked: (recordingId) => uploader?.isTracked(recordingId) ?? false,
  })
  uploader = createNativeSessionRecordingUploader({
    storage: SecureStorage,
    queueKeyPrefix: SESSION_RECORDING_UPLOAD_QUEUE_KEY_PREFIX,
    getUserId: currentUserId,
    startUpload: startSessionRecordingUpload,
    completeUpload: completeSessionRecordingUpload,
    getRecordingStatus: async (sessionUuid, recordingId) => {
      const { items } = await listSessionRecordings(sessionUuid)
      return items.find((item) => item.recordingId === recordingId)?.status ?? null
    },
    join: async (recordingId) => {
      const joined = await joinRecording(recordingId)
      return { path: joined.path, bytes: joined.bytes }
    },
    uploadPart: uploadRecordingPart,
    discardLocal: (recordingId) => recorder.discardLocal(recordingId),
    getNetworkStatus,
    onNetworkChange,
  })

  registerSessionRecordingAdapters({ recorder, uploader })
  const queue = uploader
  onSessionRecorderDeviceWipe(() => queue.resetForSignOut())
  void queue.start().catch(() => undefined)

  const sweep = () => void sweepProductionRecordings(AUDIO_MAX_AGE_MS).catch(() => undefined)
  sweep()
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'visible') sweep()
  })
}
